import { fetchMe, authorizedFetch, getToken } from './session';
import { fetchCatalog, fetchInventory, resolvePlayerTag, type CatalogItem } from './catalog';
import { escapeHtml as esc } from './format';
import { icon } from './icons';
import type { Navigate } from './router';

// A persistent floating panel (owner decision, 2026-08-11: "like the Rocket
// League social system"; floating-launcher placement 2026-08-14) — friends,
// presence, invites, and the player's own equipped-cosmetics header,
// reachable from any page without leaving it. Mounted once into
// #social-overlay-root (a sibling of #app in index.html, outside the
// router's container), so navigating between pages — which wipes and
// rebuilds #app on every hash change — never touches this. The launcher is a
// floating action button fixed to the bottom-right corner, always present
// (not just page-shell chrome), and opens the panel upward from that corner.
//
// Deliberately no in-game messaging (owner decision) — invite-to-match
// (the existing direct-challenge flow) is the only interaction beyond the
// friend-request lifecycle itself.

interface Player {
  id: string;
  username: string;
}

interface Friend extends Player {
  presence: 'online' | 'offline' | null;
  lastSeen: number | null;
}

/** Which game a direct challenge opens (mirrors the friends page and the worker's own set). */
type ChallengeGame = 'duel' | 'stack-tower';

function gameLabel(game: ChallengeGame): string {
  return game === 'stack-tower' ? 'Stack Tower' : 'Stop the Clock';
}

interface SocialBoard {
  friends: Friend[];
  incoming: { id: string; user: Player }[];
  outgoing: { id: string; user: Player }[];
  blocked: Player[];
  incomingChallenges: { id: string; from: Player; game: ChallengeGame }[];
  outgoingChallenges: { id: string; to: Player; code: string; game: ChallengeGame }[];
  now: number;
}

let root: HTMLElement | null = null;
let navigateRef: Navigate | null = null;
let isOpen = false;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let statusMessage: { text: string; ok: boolean } | null = null;

export function initSocialOverlay(navigate: Navigate): void {
  navigateRef = navigate;
  root = document.getElementById('social-overlay-root');
  if (!root) return;
  // Attached once, directly on `root` — `root` itself is never replaced
  // (only its `innerHTML`), so this survives every re-render and correctly
  // catches a click anywhere in the panel (add-friend form, profile header)
  // that isn't inside `bindBoardActions`'s own `listMount`-scoped listener.
  root.addEventListener('click', (event) => {
    if (!root || (event.target as HTMLElement).closest('.friend-more')) return;
    closeAllMoreMenus(root);
  });
  render();
}

export function toggleSocialOverlay(): void {
  isOpen = !isOpen;
  // render() must run first — it builds the DOM (including #social-overlay-list
  // and #social-overlay-profile) that loadBoard/loadProfileHeader look up by
  // id. Firing the loads first was a real bug: they'd query for elements
  // that didn't exist yet, no-op silently, and the panel would sit on
  // "Loading…" forever.
  render();
  if (isOpen) {
    void pingPresence();
    void loadBoard();
    void loadProfileHeader();
    if (!heartbeatTimer) heartbeatTimer = setInterval(pingPresence, 60_000);
  }
}

function presenceDot(presence: Friend['presence']): string {
  const online = presence === 'online';
  const label = online ? 'Online' : 'Offline';
  return `<span class="presence-dot${online ? ' presence-dot--online' : ''}" role="img" aria-label="${label}" title="${label}"></span>`;
}

function relativeTime(ms: number, nowMs: number): string {
  const diff = Math.max(0, nowMs - ms);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function lastSeenLabel(f: Friend, nowMs: number): string {
  if (f.presence === 'online') return 'Online';
  if (f.lastSeen === null) return 'Offline';
  return `Last online ${relativeTime(f.lastSeen, nowMs)}`;
}

function friendRow(f: Friend, nowMs: number): string {
  return `
    <li class="friend">
      <span class="friend__who">${presenceDot(f.presence)}<span class="friend__id"><strong>${esc(f.username)}</strong><span class="friend__note">${lastSeenLabel(f, nowMs)}</span></span></span>
      <span class="friend__actions">
        <button class="button button--primary" data-action="invite" data-user="${f.id}">Stop the Clock</button>
        <button class="button button--primary" data-action="invite-stack-tower" data-user="${f.id}">Stack Tower</button>
        <span class="friend-more">
          <button class="button button--secondary" type="button" data-action="more-toggle" aria-haspopup="true" aria-expanded="false">More&nbsp;▾</button>
          <div class="friend-more__menu" role="menu" hidden>
            <button type="button" class="friend-more__item" role="menuitem" data-action="remove" data-user="${f.id}" data-username="${esc(f.username)}">Remove friend</button>
          </div>
        </span>
      </span>
    </li>`;
}

function requestRow(r: { id: string; user: Player }, incoming: boolean): string {
  const actions = incoming
    ? `<button class="button button--primary" data-action="accept" data-id="${r.id}">Accept</button>
       <button class="button button--secondary" data-action="decline" data-id="${r.id}">Decline</button>`
    : `<button class="button button--secondary" data-action="cancel-request" data-user="${r.user.id}">Cancel</button>`;
  return `
    <li class="friend">
      <span class="friend__who"><strong>${esc(r.user.username)}</strong></span>
      <span class="friend__actions">${actions}</span>
    </li>`;
}

function incomingChallengeRow(c: { id: string; from: Player; game: ChallengeGame }): string {
  return `
    <li class="friend">
      <span class="friend__who"><span class="friend__id"><strong>${esc(c.from.username)}</strong><span class="friend__note">wants ${esc(gameLabel(c.game))}</span></span></span>
      <span class="friend__actions">
        <button class="button button--primary" data-action="accept-challenge" data-id="${c.id}" data-game="${c.game}">Accept</button>
        <button class="button button--secondary" data-action="decline-challenge" data-id="${c.id}">Decline</button>
      </span>
    </li>`;
}

function outgoingChallengeRow(c: { id: string; to: Player; code: string; game: ChallengeGame }): string {
  return `
    <li class="friend">
      <span class="friend__who"><span class="friend__id"><strong>${esc(c.to.username)}</strong><span class="friend__note">${esc(gameLabel(c.game))} · room ${esc(c.code)}</span></span></span>
      <span class="friend__actions">
        <button class="button button--primary" data-action="open-room" data-code="${esc(c.code)}" data-game="${c.game}">Open room</button>
        <button class="button button--secondary" data-action="cancel-challenge" data-id="${c.id}">Cancel</button>
      </span>
    </li>`;
}

function section(title: string, rows: string[]): string {
  if (rows.length === 0) return '';
  return `<h3 class="social-overlay__heading">${title}</h3><ul class="friend-list">${rows.join('')}</ul>`;
}

function renderBoard(board: SocialBoard): string {
  const empty =
    board.friends.length === 0 &&
    board.incoming.length === 0 &&
    board.outgoing.length === 0 &&
    board.incomingChallenges.length === 0 &&
    board.outgoingChallenges.length === 0;
  if (empty) {
    return `<p class="settings-hint">No friends yet. Add someone by username above.</p>`;
  }
  return [
    section('Invites', board.incomingChallenges.map(incomingChallengeRow)),
    section('Friend requests', board.incoming.map((r) => requestRow(r, true))),
    section(`Friends (${board.friends.length})`, board.friends.map((f) => friendRow(f, board.now))),
    section('Sent invites', board.outgoingChallenges.map(outgoingChallengeRow)),
    section('Sent requests', board.outgoing.map((r) => requestRow(r, false))),
  ].join('');
}

async function pingPresence(): Promise<void> {
  try {
    await authorizedFetch('/api/social/presence', { method: 'POST' });
  } catch {
    /* best-effort */
  }
}

async function loadBoard(): Promise<void> {
  const listMount = root?.querySelector<HTMLDivElement>('#social-overlay-list');
  if (!listMount) return;
  try {
    const board = await authorizedFetch<SocialBoard>('/api/social');
    listMount.innerHTML = renderBoard(board);
    bindBoardActions(listMount);
  } catch {
    listMount.innerHTML = `<p class="attempt-list__empty">Could not load friends.</p>`;
  }
}

async function post(path: string, bodyData: Record<string, unknown>): Promise<Record<string, unknown>> {
  return authorizedFetch<Record<string, unknown>>(path, { method: 'POST', body: JSON.stringify(bodyData) });
}

function openRoom(code: string, game: ChallengeGame): void {
  isOpen = false;
  render();
  // A Stack Tower challenge must open the Stack Tower room, not the timing
  // duel — routing every accepted challenge to `play/online` regardless of
  // game is what made a Block Stack invite drop the two players into
  // different, mismatched rooms (owner report 2026-08-18). The friends page
  // already routed by game; this brings the floating overlay in line.
  const route = game === 'stack-tower' ? 'stack-tower-online' : 'play/online';
  navigateRef?.(`${route}?code=${encodeURIComponent(code)}`);
}

/** At most one friend's "More" menu open at a time; any other click in the list closes it. */
function closeAllMoreMenus(listMount: HTMLElement): void {
  listMount.querySelectorAll<HTMLElement>('.friend-more__menu').forEach((menu) => {
    menu.hidden = true;
  });
  listMount.querySelectorAll<HTMLButtonElement>('[data-action="more-toggle"]').forEach((toggle) => {
    toggle.setAttribute('aria-expanded', 'false');
  });
}

function bindBoardActions(listMount: HTMLElement): void {
  // Closes an open "More" menu on any click in the list; the toggle's own
  // handler below calls stopPropagation() so opening one doesn't immediately
  // close itself via this same listener.
  listMount.addEventListener('click', () => closeAllMoreMenus(listMount));

  listMount.querySelectorAll<HTMLButtonElement>('[data-action="more-toggle"]').forEach((toggle) => {
    toggle.addEventListener('click', (event) => {
      event.stopPropagation();
      const menu = toggle.closest<HTMLElement>('.friend-more')!.querySelector<HTMLElement>('.friend-more__menu')!;
      const wasOpen = !menu.hidden;
      closeAllMoreMenus(listMount);
      if (!wasOpen) {
        menu.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  });

  listMount.querySelectorAll<HTMLButtonElement>('button[data-action]').forEach((target) => {
    if (target.dataset.action === 'more-toggle') return;
    target.addEventListener('click', async () => {
      const action = target.dataset.action;
      const id = target.dataset.id;
      const userId = target.dataset.user;
      try {
        switch (action) {
          case 'accept':
            await post('/api/social/respond', { id, action: 'accept' });
            break;
          case 'decline':
            await post('/api/social/respond', { id, action: 'decline' });
            break;
          case 'cancel-request':
            await post('/api/social/remove', { userId });
            break;
          case 'remove': {
            const name = target.dataset.username ?? 'this friend';
            if (!window.confirm(`Remove ${name} as a friend?`)) return;
            await post('/api/social/remove', { userId });
            break;
          }
          case 'invite': {
            const res = await post('/api/social/challenge', { userId, game: 'duel' });
            if (typeof res.code === 'string') {
              openRoom(res.code, 'duel');
              return;
            }
            break;
          }
          case 'invite-stack-tower': {
            const res = await post('/api/social/challenge', { userId, game: 'stack-tower' });
            if (typeof res.code === 'string') {
              openRoom(res.code, 'stack-tower');
              return;
            }
            break;
          }
          case 'accept-challenge': {
            const res = await post('/api/social/challenge/respond', { id, action: 'accept' });
            if (typeof res.code === 'string') {
              openRoom(res.code, res.game === 'stack-tower' ? 'stack-tower' : 'duel');
              return;
            }
            break;
          }
          case 'decline-challenge':
            await post('/api/social/challenge/respond', { id, action: 'decline' });
            break;
          case 'cancel-challenge':
            await post('/api/social/challenge/respond', { id, action: 'cancel' });
            break;
          case 'open-room':
            if (target.dataset.code) openRoom(target.dataset.code, target.dataset.game === 'stack-tower' ? 'stack-tower' : 'duel');
            return;
          default:
            return;
        }
        await loadBoard();
      } catch (error) {
        statusMessage = { text: error instanceof Error ? error.message : 'Action failed.', ok: false };
        render();
      }
    });
  });
}

async function addFriend(): Promise<void> {
  const input = root?.querySelector<HTMLInputElement>('#social-overlay-add-input');
  const username = input?.value.trim();
  if (!username) return;
  try {
    await post('/api/social/request', { username });
    if (input) input.value = '';
    statusMessage = { text: `Request sent to ${username}.`, ok: true };
    await loadBoard();
  } catch (error) {
    statusMessage = { text: error instanceof Error ? error.message : 'Could not send request.', ok: false };
  }
  render();
}

// --- Profile header: the player's own equipped emblem + call sign, ---
// --- composited client-side from the same catalog/inventory data ---
// --- Inventory and Store already fetch (see catalog.ts). ---
async function loadProfileHeader(): Promise<void> {
  const headMount = root?.querySelector<HTMLDivElement>('#social-overlay-profile');
  if (!headMount) return;
  try {
    const [me, catalog, inventory] = await Promise.all([fetchMe(), fetchCatalog(), fetchInventory()]);
    if (!me) return;
    const byId = new Map<string, CatalogItem>(catalog.items.map((i) => [i.id, i]));
    const { emblemImage, plateImage, callSignText, bannerImage } = resolvePlayerTag(byId, inventory.equipped);

    headMount.innerHTML = `
      <div class="social-profile"${bannerImage ? ` style="background-image:url('${bannerImage}')"` : ''}>
        <div class="social-profile__emblem" style="${emblemImage ? `background-image:url('${emblemImage}');` : ''}"></div>
        <div class="social-profile__id">
          <strong>${esc(me.user.username)}</strong>
          <div class="social-profile__callsign" style="${plateImage ? `background-image:url('${plateImage}');` : ''}">
            <span>${callSignText ? esc(callSignText) : ''}</span>
          </div>
        </div>
      </div>
    `;
  } catch {
    /* profile header is a nice-to-have; the friends board below still works without it */
  }
}

function fabMarkup(): string {
  return `
    <button class="social-fab" type="button" data-action="toggle-social" aria-label="${isOpen ? 'Close friends' : 'Friends'}" aria-expanded="${isOpen}">
      ${icon('people')}
    </button>
  `;
}

function render(): void {
  if (!root) return;

  if (!isOpen) {
    root.innerHTML = fabMarkup();
    root.querySelector('[data-action="toggle-social"]')?.addEventListener('click', toggleSocialOverlay);
    return;
  }

  if (!getToken()) {
    root.innerHTML = `
      ${fabMarkup()}
      <div class="social-overlay-backdrop" data-overlay-close></div>
      <aside class="social-overlay" aria-label="Friends">
        <p class="settings-hint">Sign in to see friends.</p>
      </aside>
    `;
    root.querySelector('[data-overlay-close]')?.addEventListener('click', toggleSocialOverlay);
    root.querySelector('[data-action="toggle-social"]')?.addEventListener('click', toggleSocialOverlay);
    return;
  }

  root.innerHTML = `
    ${fabMarkup()}
    <div class="social-overlay-backdrop" data-overlay-close></div>
    <aside class="social-overlay" aria-label="Friends">
      <div id="social-overlay-profile"><p class="settings-hint">Loading…</p></div>
      <div class="social-overlay__add">
        <input class="join-input" id="social-overlay-add-input" maxlength="20" placeholder="Username" aria-label="Username to add as a friend" autocomplete="off" />
        <button class="button button--primary" type="button" data-action="add-friend">Add</button>
      </div>
      ${statusMessage ? `<p class="form-error" data-tone="${statusMessage.ok ? 'ok' : 'error'}">${esc(statusMessage.text)}</p>` : ''}
      <div id="social-overlay-list" class="social-overlay__list"><p class="settings-hint">Loading…</p></div>
    </aside>
  `;

  root.querySelector('[data-overlay-close]')?.addEventListener('click', toggleSocialOverlay);
  root.querySelector('[data-action="toggle-social"]')?.addEventListener('click', toggleSocialOverlay);
  root.querySelector('[data-action="add-friend"]')?.addEventListener('click', () => void addFriend());
  root.querySelector<HTMLInputElement>('#social-overlay-add-input')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void addFriend();
  });
}
